import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Avatar, AvatarGroup, Box, Button, Center, Circle, Drawer, Heading, HStack, Icon, IconButton, Input, Link, Portal, Presence, Separator, Spinner, Text, VStack, Wrap } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { useBoardSeatCounts } from '@src/components/sco/Advisors/hooks/useBoardSeatCounts';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorMarketplaceActionButton } from '@src/components/sco/Advisors/shared/AdvisorMarketplaceActionButton';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { advisorMarketplaceActions } from '@src/components/sco/Advisors/utils/advisorMarketplaceActions';
import { ChatContextProvider } from '@src/components/sco/Chat/ChatContext';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import { paywallPropsForMessagingTarget } from '@src/components/sco/Chat/directMessagePaywall';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { MaxIntroSentModal } from '@src/components/sco/Matching/UserMatchSendMessageButton';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { ScoAdvisorProfile, ScoCompany, ScoThread, ScoUser, ScoUserExperience, ScoUserSkill, UpsertChatCommentInput, UpsertChatCommentPayload, UpsertDirectChatThreadInput, UpsertDirectChatThreadPayload } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useSSRStatus } from '@src/hooks/useSSRStatus.ts';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { ensureValidHref } from '@src/lib/startups-url';
import moment from 'moment/moment';
import { parseAsString, useQueryState } from 'nuqs';
import { Fragment, ReactNode, useState } from 'react';
import { LuArrowRight, LuArrowUpRight, LuLockKeyhole, LuX } from 'react-icons/lu';
type UserProfileProps = {
  userSlug: string;
  /**
   * When false, skip setting Members/Founders breadcrumbs so a parent shell
   * (e.g. Advisors profile) can own the trail. Still sets `activeContent.item`.
   * @default true
   */
  manageBreadcrumbs?: boolean;
  /** Tighter padding and no page wash — nested in Advisors hub. */
  embedded?: boolean;
};
export const UserProfile = (props: UserProfileProps) => {
  const {
    userSlug,
    manageBreadcrumbs = true,
    embedded = false
  } = props;
  const activeContent = useActiveContentContext();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const {
    currentUser,
    currentUserHasContentPermission
  } = useCurrentUser();
  const [, setPrompt] = useQueryState('prompt', parseAsString);
  const canAccessChat = currentUserHasContentPermission('canAccessChat');
  const query = useQuery(gql`
      query GroupMemberUser($userSlug: String) {
        groupMemberUser(userSlug: $userSlug) {
          id
          userId
          bannedAt
          firstName
          lastName
          fullName
          displayName
          bio
          shortBio
          title
          linkedin
          twitter
          lastActiveAt
          location
          pictureUrl
          profileVisibilityKey
          userSkills(order: [["experienceStrength", "desc"]]) {
            id
            userSkillId
            title
            description
            experienceNotes
            experienceStrength
          }
          userExperiences {
            id
            userExperienceId
            experienceTypeKey
            accomplishmentDescription
            accomplishmentTypeKeys
            createdAt
            endDate
            industryKeys
            organizationName
            positionTitle
            positionDescription
            startDate
          }
          slug
          isAdvisor
          isToolUser
          advisorProfile {
            id
            advisorProfileId
            statusKey
            isApproved
            warmIntroKeys
            warmIntros {
              id
              key
              title
            }
            availabilityHoursPerMonth
            responsesCount
            foundersHelpedCount
            hourlyRate
            canAcceptSessions
            bioForAdvising
            advisingNote
            headline
            categories {
              id
              key
              title
            }
          }
          company {
            id
            companyId
            elevatorPitch
            logoUrl
            name
            url
            location
            annualRevenue {
              id
              title
            }
            fundingRaised {
              id
              title
            }
            fundingStage {
              id
              title
            }
            headcount {
              id
              title
            }
            industries {
              id
              title
            }
            stage {
              id
              title
            }
            yearFounded
          }
        }
      }
    `, {
    variables: {
      userSlug
    }
  });
  const user = query.data?.groupMemberUser && !query.data.groupMemberUser?.bannedAt ? query.data.groupMemberUser : undefined;
  let profileIsVisible = false;
  if (user?.bannedAt) {
    profileIsVisible = false;
  } else if (user?.profileVisibilityKey === 'public') {
    profileIsVisible = true;
  } else if (user?.profileVisibilityKey === 'private' && currentUser?.isStartupsUnlimitedMember) {
    profileIsVisible = true;
  } else if (user?.profileVisibilityKey === 'hidden' && (currentUser?.userId == user?.userId || currentUser?.isAdmin)) {
    profileIsVisible = false;
  } else {
    profileIsVisible = true;
  }
  useSSRStatus(!profileIsVisible ? 410 : 200);
  useUniversalEffect(() => {
    activeContent.setItem(profileIsVisible ? user : null);
    if (manageBreadcrumbs) {
      activeContent.setBreadcrumbs([{
        title: 'Members',
        href: `/members`
      }, {
        title: 'Founders',
        href: `/founders`
      }, {
        title: !profileIsVisible ? 'Private' : user?.fullName || 'Loading...',
        href: `/members/${user?.userId}`
      }]);
    }
    return () => {
      activeContent.setItem(null);
    };
  }, [user?.userId, profileIsVisible, manageBreadcrumbs]);
  if (!profileIsVisible) {
    return <VStack align={'center'} bg={'bg.subtle'} gap={8} height={'full'} justify={'center'} minHeight={'full'} padding={12} paddingBottom={24} width={'full'}>
        <Icon asChild boxSize={'3em'} color={'colorPalette.border'}>
          <LuLockKeyhole />
        </Icon>
        <Heading fontWeight={'600'} size={getBreakpointValue(['xl', '2xl', '3xl'])}>
          This profile is private.
        </Heading>
        <Button asChild variant={'outline'}>
          <NextLink href={'/members'}>
            View Other Members
            <LuArrowRight />
          </NextLink>
        </Button>
      </VStack>;
  }
  if (query.loading) {
    return <Center bg={'bg.subtle'} minHeight={'full'} padding={8} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (!query.data?.groupMemberUser || query.data.groupMemberUser?.bannedAt) {
    if (query.error) {
      return <Center bg={'bg.subtle'} minHeight={'full'} padding={8} width={'full'}>
          <Text>
            {'Error loading profile: '}
            {query.error.toString()}
          </Text>
        </Center>;
    } else {
      return <Center bg={'bg.subtle'} minHeight={'full'} padding={8} width={'full'}>
          <Text>Error loading profile: Unknown</Text>
        </Center>;
    }
  }
  const isSelf = user.userId === currentUser?.userId;
  const skills = [...(user.userSkills || [])].sort((a, b) => (b.experienceStrength ?? 0) - (a.experienceStrength ?? 0));
  const experiences = sortExperiences(user.userExperiences);
  const sections: {
    key: string;
    label: string;
    button?: ReactNode;
    body: ReactNode;
  }[] = [];
  if (user.bio || isSelf) {
    sections.push({
      key: 'about',
      label: `About ${user.firstName}`,
      button: isSelf ? <Button size={'xs'} onClick={() => setPrompt('user-edit-profile')}>
          Edit
        </Button> : null,
      body: user.bio ? <MarkdownRenderer boxProps={{
        lineHeight: 'tall',
        maxWidth: '42rem'
      }} preset={'ugc'}>
          {user.bio.replaceAll('\n\n', '\n<br>')}
        </MarkdownRenderer> : <EmptySectionHint>Add a bio so members know who you are.</EmptySectionHint>
    });
  }
  if (isSelf || companyHasDisplayableContent(user.company)) {
    sections.push({
      key: 'company',
      label: 'Company',
      button: isSelf ? <Button size={'xs'} onClick={() => setPrompt('user-edit-startup')}>
          Edit
        </Button> : null,
      body: companyHasDisplayableContent(user.company) ? <CompanyLetterhead company={user.company} /> : <EmptySectionHint>Add your company to this profile.</EmptySectionHint>
    });
  }
  if (skills.length > 0 || isSelf) {
    sections.push({
      key: 'skills',
      label: 'Skills',
      button: isSelf ? <Button size={'xs'} onClick={() => setPrompt('user-edit-skills')}>
          Edit
        </Button> : null,
      body: skills.length > 0 ? <SkillRankList skills={skills} /> : <EmptySectionHint>Add skills so members know how you can help.</EmptySectionHint>
    });
  }
  if (experiences.length > 0 || isSelf) {
    sections.push({
      key: 'experience',
      label: 'Experience',
      button: isSelf ? <Button size={'xs'} onClick={() => setPrompt('user-edit-experiences')}>
          Edit
        </Button> : null,
      body: experiences.length > 0 ? <ExperienceSplit experiences={experiences} /> : <EmptySectionHint>Add roles and accomplishments.</EmptySectionHint>
    });
  }
  return <VStack align={'stretch'} bg={embedded ? undefined : 'bg.subtle'} gap={0} minHeight={'full'} width={'full'}>
      <Box paddingBottom={embedded ? 8 : 20} paddingTop={embedded ? 0 : getBreakpointValue([8, 8, 10])} paddingX={embedded ? 0 : getBreakpointValue([4, 6, 8])} width={'full'}>
        <VStack align={'stretch'} gap={getBreakpointValue([6, 8, 10])} maxWidth={'container.md'} mx={'auto'} width={'full'}>
          <HStack align={'flex-start'} gap={getBreakpointValue([4, 5, 8])} wrap={'wrap'}>
            <Box flexShrink={0}>
              <UserAvatar disablePopover={true} disablePresence={true} noLink={true} user={user} avatarProps={{
              // borderRadius: '2xl',
              boxSize: getBreakpointValue(['4.25em', '5em', '5.75em']),
              overflow: 'hidden'
            }} />
            </Box>
            <VStack align={'stretch'} flex={1} flexGrow={1} gap={4}>
              <VStack align={'stretch'} gap={2}>
                <Heading letterSpacing={'tighter'} size={getBreakpointValue(['3xl', '4xl', '5xl'])}>
                  {user.fullName}
                </Heading>
                {user.shortBio && <Text color={'fg/80'} fontFamily={'longform'} fontWeight={'medium'} lineClamp={3} lineHeight={'short'} textStyle={getBreakpointValue(['lg', 'xl', '2xl'])}>
                    {user.shortBio}
                  </Text>}
              </VStack>
              <MetaLine canAccessChat={canAccessChat} user={user} />
            </VStack>
          </HStack>
          {!isSelf && <SSRGate disableSSR={true}>
              <SendIntroForm user={user} />
            </SSRGate>}
          <AdvisorStrip advisorProfile={user.advisorProfile} userId={user.userId} />

          {sections.length > 0 && <VStack align={'stretch'} gap={0} width={'full'}>
              {sections.map((section, index) => <Box key={section.key} id={section.key}>
                  {index > 0 && <Separator borderColor={'border'} marginY={getBreakpointValue([8, 10, 12])} />}
                  <EditorialSection button={section.button} label={section.label}>
                    {section.body}
                  </EditorialSection>
                </Box>)}
            </VStack>}
        </VStack>
      </Box>
    </VStack>;
};
const hasDisplayValue = (value?: string | number | null) => {
  if (value == null) {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  return normalized.length > 0 && normalized !== 'n/a' && normalized !== '-';
};
const displayExternalLabel = (value: string) => value.replace(/^https?:\/\//i, '').replace(/\/$/, '');
const companyHasDisplayableContent = (company?: ScoCompany | null) => {
  if (!company) {
    return false;
  }
  return Boolean(hasDisplayValue(company.name) || hasDisplayValue(company.elevatorPitch) || company.industries?.some(industry => hasDisplayValue(industry.title)) || hasDisplayValue(company.location) || hasDisplayValue(company.url) || hasDisplayValue(company.headcount?.title) || hasDisplayValue(company.stage?.title) || hasDisplayValue(company.fundingStage?.title) || hasDisplayValue(company.fundingRaised?.title) || hasDisplayValue(company.annualRevenue?.title) || hasDisplayValue(company.yearFounded));
};
const formatExperienceDates = (startDate?: string | null, endDate?: string | null) => {
  const start = startDate ? moment(startDate).format('YYYY') : null;
  if (!start && !endDate) {
    return '';
  }
  const end = endDate ? moment(endDate).format('YYYY') : 'Present';
  if (!start) {
    return end;
  }
  return `${start} — ${end}`;
};
const sortExperiences = (experiences?: ScoUserExperience[] | null) => [...(experiences || [])].sort((a, b) => {
  const aEnd = a.endDate ? moment(a.endDate).valueOf() : Number.POSITIVE_INFINITY;
  const bEnd = b.endDate ? moment(b.endDate).valueOf() : Number.POSITIVE_INFINITY;
  if (aEnd !== bEnd) {
    return bEnd - aEnd;
  }
  return moment(b.startDate || 0).valueOf() - moment(a.startDate || 0).valueOf();
});
const EmptySectionHint = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Box bg="colorPalette.subtle/40" borderColor="colorPalette.border/50" borderRadius="l2" borderStyle="dashed" borderWidth="1px" paddingX={4} paddingY={3}><Text color="fg.muted" textStyle="sm">{children}</Text></Box>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const MetaLine = t0 => {
  const $ = _c(25);
  const {
    canAccessChat,
    user
  } = t0;
  let parts;
  if ($[0] !== canAccessChat || $[1] !== user.lastActiveAt || $[2] !== user.linkedin || $[3] !== user.location || $[4] !== user.title || $[5] !== user.twitter) {
    parts = [];
    if (hasDisplayValue(user.title)) {
      parts.push(user.title);
    }
    if (hasDisplayValue(user.location)) {
      parts.push(user.location);
    }
    if (canAccessChat && user.lastActiveAt) {
      let t1;
      if ($[7] !== user.lastActiveAt) {
        t1 = moment(user.lastActiveAt).fromNow();
        $[7] = user.lastActiveAt;
        $[8] = t1;
      } else {
        t1 = $[8];
      }
      parts.push(`Active ${t1}`);
    }
    if (canAccessChat && hasDisplayValue(user.linkedin)) {
      let t1;
      if ($[9] !== user.linkedin) {
        t1 = ensureValidHref(user.linkedin);
        $[9] = user.linkedin;
        $[10] = t1;
      } else {
        t1 = $[10];
      }
      let t2;
      if ($[11] !== user.linkedin) {
        t2 = displayExternalLabel(user.linkedin);
        $[11] = user.linkedin;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      let t3;
      if ($[13] !== t1 || $[14] !== t2) {
        t3 = <Link color="fg.muted" href={t1} target="_blank" textDecoration="underline" textDecorationColor="border" textUnderlineOffset="3px">{t2}</Link>;
        $[13] = t1;
        $[14] = t2;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      parts.push(t3);
    }
    if (canAccessChat && hasDisplayValue(user.twitter)) {
      let t1;
      if ($[16] !== user.twitter) {
        t1 = ensureValidHref(user.twitter);
        $[16] = user.twitter;
        $[17] = t1;
      } else {
        t1 = $[17];
      }
      let t2;
      if ($[18] !== user.twitter) {
        t2 = displayExternalLabel(user.twitter);
        $[18] = user.twitter;
        $[19] = t2;
      } else {
        t2 = $[19];
      }
      let t3;
      if ($[20] !== t1 || $[21] !== t2) {
        t3 = <Link color="fg.muted" href={t1} target="_blank" textDecoration="underline" textDecorationColor="border" textUnderlineOffset="3px">{t2}</Link>;
        $[20] = t1;
        $[21] = t2;
        $[22] = t3;
      } else {
        t3 = $[22];
      }
      parts.push(t3);
    }
    $[0] = canAccessChat;
    $[1] = user.lastActiveAt;
    $[2] = user.linkedin;
    $[3] = user.location;
    $[4] = user.title;
    $[5] = user.twitter;
    $[6] = parts;
  } else {
    parts = $[6];
  }
  if (parts.length === 0) {
    return null;
  }
  let t1;
  if ($[23] !== parts) {
    t1 = <Text color="fg.muted" textStyle="sm">{parts.map(_temp2)}</Text>;
    $[23] = parts;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  return t1;
};
const EditorialSection = t0 => {
  const $ = _c(8);
  const {
    label,
    button,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Heading size="md">{label}</Heading>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== button || $[3] !== t1) {
    t2 = <HStack align="baseline" gap={3} justify="space-between">{t1}{button}</HStack>;
    $[2] = button;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <VStack align="stretch" gap={4}>{t2}{children}</VStack>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const AdvisorStrip = t0 => {
  const $ = _c(99);
  const {
    advisorProfile,
    userId
  } = t0;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const {
    currentUser
  } = useCurrentUser();
  const paths = useAdvisorsPaths();
  const [inviteOpen, setInviteOpen] = useState(false);
  const {
    counts: seatCounts
  } = useBoardSeatCounts();
  const hasOpenSeats = seatCounts.drafts + seatCounts.searching > 0;
  const isSelf = userId === currentUser?.userId;
  let t1;
  if ($[0] !== getBreakpointValue) {
    t1 = getBreakpointValue([false, false, true]);
    $[0] = getBreakpointValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isRow = Boolean(t1);
  if (!advisorProfile?.isApproved) {
    return null;
  }
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t2;
  let t20;
  let t21;
  let t22;
  let t23;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== advisorProfile.advisingNote || $[3] !== advisorProfile.availabilityHoursPerMonth || $[4] !== advisorProfile.bioForAdvising || $[5] !== advisorProfile.canAcceptSessions || $[6] !== advisorProfile.categories || $[7] !== advisorProfile.foundersHelpedCount || $[8] !== advisorProfile.responsesCount || $[9] !== advisorProfile.warmIntros || $[10] !== getBreakpointValue || $[11] !== hasOpenSeats || $[12] !== isRow || $[13] !== isSelf || $[14] !== paths || $[15] !== userId) {
    const warmIntroLabels = advisorProfile.warmIntros?.map(_temp3).filter(Boolean) ?? [];
    const categoryLabels = advisorProfile.categories?.map(_temp4).filter(Boolean) ?? [];
    const actions = advisorMarketplaceActions({
      canAcceptSessions: advisorProfile.canAcceptSessions,
      hasOpenSeats,
      isSelf
    }).filter(_temp5);
    T2 = Box;
    t17 = "colorPalette.subtle";
    t18 = "colorPalette.border/30";
    t19 = "2xl";
    t20 = "1px";
    t21 = "green";
    t22 = "hidden";
    t23 = 0;
    T1 = HStack;
    t12 = "stretch";
    t13 = 0;
    const t24 = isRow ? "block" : "none";
    if ($[41] !== t24) {
      t14 = <Separator display={t24} orientation="vertical" />;
      $[41] = t24;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    t15 = "wrap";
    let t25;
    if ($[43] !== getBreakpointValue) {
      t25 = getBreakpointValue([5, 6, 7]);
      $[43] = getBreakpointValue;
      $[44] = t25;
    } else {
      t25 = $[44];
    }
    let t26;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <HStack gap={2.5}><Circle bg="colorPalette.solid" size="2" /><Text textStyle="labelXs">Open to advisory</Text></HStack>;
      $[45] = t26;
    } else {
      t26 = $[45];
    }
    let t27;
    if ($[46] !== advisorProfile.availabilityHoursPerMonth) {
      t27 = advisorProfile.availabilityHoursPerMonth != null && <Heading letterSpacing="tighter" size="3xl">{advisorProfile.availabilityHoursPerMonth} hrs / month</Heading>;
      $[46] = advisorProfile.availabilityHoursPerMonth;
      $[47] = t27;
    } else {
      t27 = $[47];
    }
    const t28 = categoryLabels.length > 0 && <Text color="colorPalette.fg/80" textStyle="sm">{categoryLabels.join("  \xB7  ")}</Text>;
    let t29;
    if ($[48] !== advisorProfile.advisingNote || $[49] !== advisorProfile.bioForAdvising) {
      t29 = (advisorProfile.bioForAdvising || advisorProfile.advisingNote) && <Text color="colorPalette.fg/80" fontFamily="longform" textStyle="sm">{advisorProfile.bioForAdvising || advisorProfile.advisingNote}</Text>;
      $[48] = advisorProfile.advisingNote;
      $[49] = advisorProfile.bioForAdvising;
      $[50] = t29;
    } else {
      t29 = $[50];
    }
    const t30 = warmIntroLabels.length > 0 && <VStack align="flex-start" gap={2}><Text color="colorPalette.fg/70" textStyle="sm">Makes warm intros to</Text><Wrap gap={1.5}>{warmIntroLabels.map(_temp6)}</Wrap></VStack>;
    if ($[51] !== t25 || $[52] !== t27 || $[53] !== t28 || $[54] !== t29 || $[55] !== t30) {
      t16 = <VStack align="flex-start" flex={1} gap={5} minWidth="16rem" padding={t25}>{t26}{t27}{t28}{t29}{t30}</VStack>;
      $[51] = t25;
      $[52] = t27;
      $[53] = t28;
      $[54] = t29;
      $[55] = t30;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    T0 = VStack;
    t2 = "stretch";
    t3 = "colorPalette.muted";
    t4 = 0;
    t5 = 5;
    t6 = "space-between";
    t7 = isRow ? "13.5rem" : "full";
    if ($[57] !== getBreakpointValue) {
      t8 = getBreakpointValue([5, 6]);
      $[57] = getBreakpointValue;
      $[58] = t8;
    } else {
      t8 = $[58];
    }
    t9 = isRow ? "13.5rem" : "full";
    if ($[59] !== advisorProfile.foundersHelpedCount || $[60] !== advisorProfile.responsesCount) {
      t10 = ((advisorProfile.responsesCount ?? 0) > 0 || (advisorProfile.foundersHelpedCount ?? 0) > 0) && <VStack align="flex-start" gap={4}>{(advisorProfile.responsesCount ?? 0) > 0 && <BandStat label="Responses" value={String(advisorProfile.responsesCount)} />}{(advisorProfile.foundersHelpedCount ?? 0) > 0 && <BandStat label="Startups helped" value={String(advisorProfile.foundersHelpedCount)} />}</VStack>;
      $[59] = advisorProfile.foundersHelpedCount;
      $[60] = advisorProfile.responsesCount;
      $[61] = t10;
    } else {
      t10 = $[61];
    }
    t11 = isSelf ? <Button asChild={true} size="sm" variant="outline"><NextLink href={paths.profileEdit}>Edit advisor profile</NextLink></Button> : actions.length > 0 && <VStack align="stretch" gap={2}>{actions.map(action_0 => <AdvisorMarketplaceActionButton key={action_0.key} action={action_0} paths={paths} userId={userId} onInvite={() => setInviteOpen(true)} />)}</VStack>;
    $[2] = advisorProfile.advisingNote;
    $[3] = advisorProfile.availabilityHoursPerMonth;
    $[4] = advisorProfile.bioForAdvising;
    $[5] = advisorProfile.canAcceptSessions;
    $[6] = advisorProfile.categories;
    $[7] = advisorProfile.foundersHelpedCount;
    $[8] = advisorProfile.responsesCount;
    $[9] = advisorProfile.warmIntros;
    $[10] = getBreakpointValue;
    $[11] = hasOpenSeats;
    $[12] = isRow;
    $[13] = isSelf;
    $[14] = paths;
    $[15] = userId;
    $[16] = T0;
    $[17] = T1;
    $[18] = T2;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
    $[29] = t2;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
    $[33] = t23;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    T0 = $[16];
    T1 = $[17];
    T2 = $[18];
    t10 = $[19];
    t11 = $[20];
    t12 = $[21];
    t13 = $[22];
    t14 = $[23];
    t15 = $[24];
    t16 = $[25];
    t17 = $[26];
    t18 = $[27];
    t19 = $[28];
    t2 = $[29];
    t20 = $[30];
    t21 = $[31];
    t22 = $[32];
    t23 = $[33];
    t3 = $[34];
    t4 = $[35];
    t5 = $[36];
    t6 = $[37];
    t7 = $[38];
    t8 = $[39];
    t9 = $[40];
  }
  let t24;
  if ($[62] !== T0 || $[63] !== t10 || $[64] !== t11 || $[65] !== t2 || $[66] !== t3 || $[67] !== t4 || $[68] !== t5 || $[69] !== t6 || $[70] !== t7 || $[71] !== t8 || $[72] !== t9) {
    t24 = <T0 align={t2} bg={t3} flexShrink={t4} gap={t5} justify={t6} minWidth={t7} padding={t8} width={t9}>{t10}{t11}</T0>;
    $[62] = T0;
    $[63] = t10;
    $[64] = t11;
    $[65] = t2;
    $[66] = t3;
    $[67] = t4;
    $[68] = t5;
    $[69] = t6;
    $[70] = t7;
    $[71] = t8;
    $[72] = t9;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== T1 || $[75] !== t12 || $[76] !== t13 || $[77] !== t14 || $[78] !== t15 || $[79] !== t16 || $[80] !== t24) {
    t25 = <T1 align={t12} gap={t13} separator={t14} wrap={t15}>{t16}{t24}</T1>;
    $[74] = T1;
    $[75] = t12;
    $[76] = t13;
    $[77] = t14;
    $[78] = t15;
    $[79] = t16;
    $[80] = t24;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== T2 || $[83] !== t17 || $[84] !== t18 || $[85] !== t19 || $[86] !== t20 || $[87] !== t21 || $[88] !== t22 || $[89] !== t23 || $[90] !== t25) {
    t26 = <T2 bg={t17} borderColor={t18} borderRadius={t19} borderWidth={t20} colorPalette={t21} overflow={t22} padding={t23}>{t25}</T2>;
    $[82] = T2;
    $[83] = t17;
    $[84] = t18;
    $[85] = t19;
    $[86] = t20;
    $[87] = t21;
    $[88] = t22;
    $[89] = t23;
    $[90] = t25;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setInviteOpen(false);
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  let t28;
  if ($[93] !== inviteOpen || $[94] !== userId) {
    t28 = <InviteToBoardModal advisorUserId={userId} isOpen={inviteOpen} onClose={t27} />;
    $[93] = inviteOpen;
    $[94] = userId;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  let t29;
  if ($[96] !== t26 || $[97] !== t28) {
    t29 = <>{t26}{t28}</>;
    $[96] = t26;
    $[97] = t28;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  return t29;
};
const BandStat = t0 => {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <Text fontFamily="mono" fontWeight="semibold" textStyle="2xl">{value}</Text>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <Text color="colorPalette.fg/60" textStyle="label2xs">{label}</Text>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="flex-start" gap={0}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const CompanyLetterhead = t0 => {
  const $ = _c(38);
  const {
    company
  } = t0;
  let tickerItems;
  if ($[0] !== company.annualRevenue || $[1] !== company.fundingRaised || $[2] !== company.fundingStage || $[3] !== company.headcount || $[4] !== company.industries || $[5] !== company.location || $[6] !== company.stage || $[7] !== company.yearFounded) {
    const industryTitles = company.industries?.map(_temp7).filter(hasDisplayValue) || [];
    tickerItems = [];
    if (hasDisplayValue(company.stage?.title)) {
      let t1;
      if ($[9] !== company.stage.title) {
        t1 = {
          key: "stage",
          label: "Company Stage",
          value: company.stage.title
        };
        $[9] = company.stage.title;
        $[10] = t1;
      } else {
        t1 = $[10];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.fundingStage?.title)) {
      let t1;
      if ($[11] !== company.fundingStage.title) {
        t1 = {
          key: "fundingStage",
          label: "Funding Stage",
          value: company.fundingStage.title
        };
        $[11] = company.fundingStage.title;
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.fundingRaised?.title)) {
      let t1;
      if ($[13] !== company.fundingRaised.title) {
        t1 = {
          key: "fundingRaised",
          label: "Funding Raised",
          value: company.fundingRaised.title
        };
        $[13] = company.fundingRaised.title;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.headcount?.title)) {
      let t1;
      if ($[15] !== company.headcount.title) {
        t1 = {
          key: "headcount",
          label: "Team Size",
          value: company.headcount.title
        };
        $[15] = company.headcount.title;
        $[16] = t1;
      } else {
        t1 = $[16];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.annualRevenue?.title)) {
      let t1;
      if ($[17] !== company.annualRevenue.title) {
        t1 = {
          key: "annualRevenue",
          label: "Annual Revenue",
          value: company.annualRevenue.title
        };
        $[17] = company.annualRevenue.title;
        $[18] = t1;
      } else {
        t1 = $[18];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.location)) {
      let t1;
      if ($[19] !== company.location) {
        t1 = {
          key: "location",
          label: "Location",
          value: company.location
        };
        $[19] = company.location;
        $[20] = t1;
      } else {
        t1 = $[20];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.yearFounded)) {
      const t1 = String(company.yearFounded);
      let t2;
      if ($[21] !== t1) {
        t2 = {
          key: "yearFounded",
          label: "Founded",
          value: t1
        };
        $[21] = t1;
        $[22] = t2;
      } else {
        t2 = $[22];
      }
      tickerItems.push(t2);
    }
    if (industryTitles.length > 0) {
      tickerItems.push({
        key: "industries",
        label: industryTitles.length > 1 ? "Industries" : "Industry",
        value: industryTitles.join(", ")
      });
    }
    $[0] = company.annualRevenue;
    $[1] = company.fundingRaised;
    $[2] = company.fundingStage;
    $[3] = company.headcount;
    $[4] = company.industries;
    $[5] = company.location;
    $[6] = company.stage;
    $[7] = company.yearFounded;
    $[8] = tickerItems;
  } else {
    tickerItems = $[8];
  }
  let t1;
  if ($[23] !== company.name) {
    t1 = hasDisplayValue(company.name) && <Heading size="2xl">{company.name}</Heading>;
    $[23] = company.name;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  let t2;
  if ($[25] !== company.url) {
    t2 = hasDisplayValue(company.url) && <Link href={ensureValidHref(company.url)} target="_blank"><HStack color="fg.muted" gap={1} textStyle="sm"><Text>{displayExternalLabel(company.url)}</Text><LuArrowUpRight /></HStack></Link>;
    $[25] = company.url;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  let t3;
  if ($[27] !== t1 || $[28] !== t2) {
    t3 = <HStack align="baseline" flexWrap="wrap" gap={3}>{t1}{t2}</HStack>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  let t4;
  if ($[30] !== company.elevatorPitch) {
    t4 = hasDisplayValue(company.elevatorPitch) && <MarkdownRenderer boxProps={{
      color: "fg.muted",
      lineHeight: "tall"
    }} preset="ugc">{company.elevatorPitch.replaceAll("\n\n", "\n<br>")}</MarkdownRenderer>;
    $[30] = company.elevatorPitch;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] !== tickerItems) {
    t5 = tickerItems.length > 0 && <Wrap gap={6} paddingTop={1}>{tickerItems.map(_temp8)}</Wrap>;
    $[32] = tickerItems;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  let t6;
  if ($[34] !== t3 || $[35] !== t4 || $[36] !== t5) {
    t6 = <VStack align="flex-start" gap={3}>{t3}{t4}{t5}</VStack>;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  return t6;
};
const SkillRankList = t0 => {
  const $ = _c(4);
  const {
    skills
  } = t0;
  let t1;
  if ($[0] !== skills) {
    const withNotes = skills.filter(_temp9);
    const t2 = skills.map(_temp10);
    let t3;
    if ($[2] !== t2) {
      t3 = <Wrap gap={2}>{t2}</Wrap>;
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t1 = <VStack align="stretch" gap={4} width="full">{t3}{withNotes.length > 0 && <VStack align="stretch" gap={3}>{withNotes.map(_temp11)}</VStack>}</VStack>;
    $[0] = skills;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const EXPERIENCE_GAP = 8;
const ExperienceSplit = t0 => {
  const $ = _c(6);
  const {
    experiences
  } = t0;
  let t1;
  if ($[0] !== experiences) {
    let t2;
    if ($[2] !== experiences.length) {
      t2 = (experience, index) => <HStack key={experience.id} align="flex-start" gap={5}><Text color="fg.muted" flexShrink={0} fontFamily="mono" minWidth="9rem" paddingTop="6px" textStyle="xs">{formatExperienceDates(experience.startDate, experience.endDate)}</Text><ExperienceRail isFirst={index === 0} isLast={index === experiences.length - 1} /><VStack align="flex-start" flex={1} gap={1} minWidth={0} paddingTop="1px">{hasDisplayValue(experience.experienceTypeKey) && <Text color="fg.subtle" textStyle="label2xs">{experience.experienceTypeKey}</Text>}{hasDisplayValue(experience.positionTitle) && <Text fontWeight="semibold" textStyle="lg">{experience.positionTitle}</Text>}{hasDisplayValue(experience.organizationName) && <Text color="fg.muted" textStyle="sm">{experience.organizationName}</Text>}{hasDisplayValue(experience.positionDescription) && <Text fontFamily="longform" marginTop={2} textStyle="md">{experience.positionDescription}</Text>}{hasDisplayValue(experience.accomplishmentDescription) && <Text color="fg.muted" fontFamily="longform" textStyle="sm">{experience.accomplishmentDescription}</Text>}</VStack></HStack>;
      $[2] = experiences.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = experiences.map(t2);
    $[0] = experiences;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <VStack align="stretch" gap={EXPERIENCE_GAP} width="full">{t1}</VStack>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

/** Hollow dot + 1px rail. Extends through the parent gap so entries read as one timeline. */
const ExperienceRail = t0 => {
  const $ = _c(6);
  const {
    isFirst,
    isLast
  } = t0;
  const gapVar = `var(--chakra-spacing-${EXPERIENCE_GAP})`;
  const t1 = isLast ? "calc(100% - 11px)" : `calc(-1 * ${gapVar})`;
  const t2 = isFirst ? "9px" : "0";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Box aria-hidden={true} bg="border" bottom={t1} left="calc(50% - 0.5px)" position="absolute" top={t2} width="1px" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Circle bg="bg.subtle" borderColor="fg.muted" borderWidth="1px" boxSize="2.5" marginTop="6px" position="relative" zIndex={1} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <Box alignSelf="stretch" flexShrink={0} position="relative" width="3">{t3}{t4}</Box>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
const SendIntroForm = (props: {
  user: ScoUser;
}) => {
  const {
    currentUser
  } = useCurrentUser();
  const teamPaywall = paywallPropsForMessagingTarget(props.user, currentUser);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chatThreadId, setChatThreadId] = useQueryState('chatThreadId', parseAsString);
  const [introChatMessage, setIntroChatMessage] = useState<string>('');
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const chatThreadQuery = useQuery(gql`
      query ($userSlug: String) {
        groupMemberUser(userSlug: $userSlug) {
          id
          directChatThreadWithCurrentUser {
            id
            threadId
            commentsCount
          }
        }
      }
    `, {
    variables: {
      userSlug: props.user.slug || props.user.userId
    }
  });
  const existingChatThread = chatThreadQuery.data?.groupMemberUser?.directChatThreadWithCurrentUser;
  const hasExistingConversation = (existingChatThread?.commentsCount ?? 0) > 0;
  const [upsertDirectChatThread] = useMutation<{
    upsertDirectChatThread: UpsertDirectChatThreadPayload;
  }>(gql`
    mutation upsertChatThread($input: UpsertDirectChatThreadInput!) {
      upsertDirectChatThread(input: $input) {
        thread {
          id
          threadId
        }
        viewer {
          id
          currentUser {
            id
            remainingMemberChatsCount
          }
        }
      }
    }
  `);
  const [upsertChatComment] = useMutation<UpsertChatCommentPayload>(gql`
    mutation ($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
        }
      }
    }
  `);
  const onSubmit = async () => {
    if (!introChatMessage) {
      setError('Enter a message first!');
      return;
    }
    setIsLoading(true);
    try {
      setError(null);
      const threadInput: UpsertDirectChatThreadInput = {
        memberUserIds: [props.user.userId, currentUser?.userId]
      };
      const threadResult = await upsertDirectChatThread({
        variables: {
          input: threadInput
        }
      });
      const commentInput: UpsertChatCommentInput = {
        threadId: threadResult.data.upsertDirectChatThread.thread.threadId,
        body: introChatMessage
      };
      await upsertChatComment({
        variables: {
          input: commentInput
        }
      });
      setChatThreadId(threadResult.data.upsertDirectChatThread.thread.threadId);
      chatThreadQuery.refetch();
    } catch (err) {
      setError(err);
    }
    setIsLoading(false);
  };
  if (isOpen && !existingChatThread?.threadId && currentUser?.remainingMemberChatsCount === 0) {
    return <MaxIntroSentModal open={isOpen} onClose={onClose} />;
  }
  return <>
      <PaywalledAction contentPermission={teamPaywall?.contentPermission ?? 'canAccessChat'} intent={teamPaywall ? undefined : 'member-message'} isUnlocked={teamPaywall?.isUnlocked} title={teamPaywall?.title ?? 'Upgrade to request an introduction.'} upgradeProductId={teamPaywall?.upgradeProductId} variant={'boxed'} upgradeBoxProps={{
      padding: '1em'
    }}>
        <>
          {hasExistingConversation ? <HStack bg={'colorPalette.container'} borderColor={'colorPalette.border/25'} borderRadius={'xl'} borderWidth={'1px'} gap={2} padding={1.5} paddingLeft={4} width={'full'}>
              <Text color={'fg.muted'} flex={1} textStyle={'sm'}>
                {`You already have a chat with ${props.user.firstName}.`}
              </Text>
              <Button size={'sm'} variant={'cta'} onClick={() => setChatThreadId(existingChatThread.threadId)}>
                Open Chat
              </Button>
            </HStack> : !isOpen ? <HStack bg={'colorPalette.container'} borderColor={'colorPalette.border/25'} borderRadius={'xl'} borderWidth={'1px'} gap={2} padding={1.5} paddingLeft={4} width={'full'}>
              <Input _placeholder={{
            color: 'fg.muted'
          }} autoFocus={false} bg={'transparent'} borderWidth={'0'} color={'fg'} flex={1} maxLength={500} name={'intro'} outline={'none'} padding={0} placeholder={`Write ${props.user.firstName} a note…`} shadow={'none'} textStyle={'sm'} onFocus={() => {
            onOpen();
          }} />
              <Button size={'sm'} variant={'cta'} onClick={() => onOpen()}>
                Send
              </Button>
            </HStack> : <Presence animationDuration={'moderate'} present={true} style={{
          width: '100%'
        }} animationName={{
          _open: 'slide-from-bottom, fade-in',
          _closed: 'slide-to-bottom, fade-out'
        }}>
              <VStack align={'stretch'} gap={2} marginY={2} width={'full'}>
                <Text textStyle={'badgeSm'}>Send an Introduction:</Text>
                <RichMarkdownInput autoFocus={true} placeholder={'Write your message...'} onChange={values => setIntroChatMessage(values.markdown)} />
                {error && <Text color={'fg.error'} textStyle={'sm'}>
                    {String(error)}
                  </Text>}
                <HStack justify={'flex-end'}>
                  <Text color={'fg.muted'} flexGrow={1} textStyle={'xs'}>
                    {'You can initiate '}
                    <strong>
                      {currentUser?.remainingMemberChatsCount}
                      {' more'}
                    </strong>{' '}
                    introductions today.
                  </Text>
                  <Box>
                    <Button disabled={isLoading} loading={isLoading} variant={'cta'} onClick={onSubmit}>
                      Send
                    </Button>
                  </Box>
                </HStack>
              </VStack>
            </Presence>}
        </>
      </PaywalledAction>
      <Portal>
        <Drawer.Root open={!!chatThreadId} placement={'end'} onOpenChange={e => {
        if (!e.open) {
          setChatThreadId(null);
        }
      }}>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content maxWidth={'480px'} width={'90%'}>
              <Drawer.CloseTrigger />
              <Drawer.Body bgColor={'colorPalette.container'} padding={0}>
                <ChatContextProvider activeThreadId={chatThreadId}>
                  <ChatThread
                // Tool-persona chats are tool-chat threads. The composer stays open here.
                bypassContentPermissions threadId={chatThreadId} renderCustomHeader={() => <UserProfileChatHeader threadId={chatThreadId} onClose={() => setChatThreadId(null)} />} />
                </ChatContextProvider>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Drawer.Root>
      </Portal>
    </>;
};
export const UserProfileChatHeader = props => {
  const $ = _c(65);
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($threadId: String!) {
        forum {
          id
          thread(threadId: $threadId) {
            id
            title
            threadId
            threadTypeKey
            userMemberships {
              id
              items {
                id
                user {
                  id
                  userId
                  fullName
                  pictureUrl
                }
              }
            }
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.threadId) {
    t1 = {
      variables: {
        threadId: props.threadId
      }
    };
    $[1] = props.threadId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const thread = query.data?.forum.thread;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== currentUser || $[4] !== props || $[5] !== query.error || $[6] !== thread) {
    const recipientUsers = thread ? thread.userMemberships.items.filter(membership => thread.userMemberships.items.length === 1 || membership.user.userId !== currentUser.userId).map(_temp12) : [];
    T2 = HStack;
    t8 = "center";
    t9 = "blur(10px)";
    t10 = "colorPalette.panel";
    t11 = "1px solid";
    t12 = "colorPalette.border/50";
    t13 = "3.5em";
    t14 = "flex-start";
    t15 = "1em";
    t16 = 1;
    if ($[27] !== query.error) {
      t17 = query.error && <Text>{"Error: "}{query.error.toString()}</Text>;
      $[27] = query.error;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    if ($[29] !== thread) {
      t18 = !thread && <Text>Loading...</Text>;
      $[29] = thread;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    T1 = HStack;
    t4 = "flex-start";
    t5 = "full";
    let t19;
    if ($[31] !== props) {
      t19 = () => {
        props.onClose();
      };
      $[31] = props;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <LuX />;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    if ($[34] !== t19) {
      t6 = <IconButton aria-label="Close Chat" borderRadius="md" size="sm" variant="outline" onClick={t19}>{t20}</IconButton>;
      $[34] = t19;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    t7 = <AvatarGroup>{recipientUsers.map(_temp13)}</AvatarGroup>;
    T0 = Text;
    t2 = "bold";
    t3 = recipientUsers.map(_temp14).join(", ");
    $[3] = currentUser;
    $[4] = props;
    $[5] = query.error;
    $[6] = thread;
    $[7] = T0;
    $[8] = T1;
    $[9] = T2;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
    $[18] = t18;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    T0 = $[7];
    T1 = $[8];
    T2 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t13 = $[13];
    t14 = $[14];
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
    t18 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t19;
  if ($[36] !== T0 || $[37] !== t2 || $[38] !== t3) {
    t19 = <T0 fontWeight={t2}>{t3}</T0>;
    $[36] = T0;
    $[37] = t2;
    $[38] = t3;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== T1 || $[41] !== t19 || $[42] !== t4 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7) {
    t20 = <T1 justify={t4} width={t5}>{t6}{t7}{t19}</T1>;
    $[40] = T1;
    $[41] = t19;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const t21 = `/inbox?threadId=${thread?.threadId}`;
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <LuArrowUpRight />;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21) {
    t23 = <div><Button asChild={true} size="sm" variant="outline"><NextLink href={t21} target="_blank">Open in Chat{t22}</NextLink></Button></div>;
    $[48] = t21;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== T2 || $[51] !== t10 || $[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t14 || $[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t18 || $[60] !== t20 || $[61] !== t23 || $[62] !== t8 || $[63] !== t9) {
    t24 = <T2 align={t8} backdropFilter={t9} bgColor={t10} borderBottom={t11} borderBottomColor={t12} height={t13} justify={t14} padding={t15} zIndex={t16}>{t17}{t18}{t20}{t23}</T2>;
    $[50] = T2;
    $[51] = t10;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t20;
    $[61] = t23;
    $[62] = t8;
    $[63] = t9;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  return t24;
};
function _temp2(part, index) {
  return <Fragment key={index}>{index > 0 ? "  \xB7  " : null}{part}</Fragment>;
}
function _temp3(item) {
  return item.title;
}
function _temp4(item_0) {
  return item_0.title;
}
function _temp5(action) {
  return action.key !== "message" && action.key !== "profile";
}
function _temp6(label) {
  return <SpecialtyTag key={label} colorPalette="cyan" label={label} />;
}
function _temp7(industry) {
  return industry.title;
}
function _temp8(item) {
  return <VStack key={item.key} align="flex-start" gap={1} minWidth="8rem"><Text color="fg.subtle" textStyle="labelXs">{item.label}</Text><Text fontWeight="medium" textStyle="sm">{item.value}</Text></VStack>;
}
function _temp9(skill) {
  return hasDisplayValue(skill.experienceNotes);
}
function _temp10(skill_0) {
  return <SpecialtyTag key={skill_0.id || skill_0.userSkillId} label={skill_0.title} />;
}
function _temp11(skill_1) {
  return <VStack key={skill_1.id || skill_1.userSkillId} align="stretch" gap={0.5}><Text fontWeight="medium" textStyle="sm">{skill_1.title}</Text><Text color="fg.muted" fontFamily="longform" textStyle="sm">{skill_1.experienceNotes}</Text></VStack>;
}
function _temp12(membership_0) {
  return membership_0.user;
}
function _temp13(user) {
  return <Avatar.Root key={user.userId} border="none" size="xs"><Avatar.Fallback /><Avatar.Image src={user.pictureUrl} /></Avatar.Root>;
}
function _temp14(user_0) {
  return user_0.fullName;
}